/**
 * Vendor Analytics Dashboard
 * Growth tier: Overview (KPIs, trend chart, traffic sources, product breakdown)
 * Pro tier: Booking Funnel, Booking Attribution, AI Conversion (with comparison),
 *           Reliability (with trend)
 */

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { api } from '@/services/api'
import FeatureGate from '@/components/FeatureGate'
import { CardSkeleton } from '@/components/SkeletonLoader'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts'

// ─── Shared types ─────────────────────────────────────────────────────────

interface FilterState {
  days: number
  productId: string
  aggregation: 'daily' | 'weekly' | 'monthly'
  startDate: string
  endDate: string
  useCustomRange: boolean
}

const defaultFilters: FilterState = {
  days: 30, productId: '', aggregation: 'daily',
  startDate: '', endDate: '', useCustomRange: false,
}

// ─── Filter Bar ───────────────────────────────────────────────────────────

const btnStyle = (active: boolean) => ({
  padding: '0.35rem 0.75rem', fontSize: 'var(--text-xs)', border: 'none', borderRadius: '4px', cursor: 'pointer',
  fontWeight: active ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)',
  background: active ? 'var(--color-tixxly-navy)' : 'var(--color-gray-100)',
  color: active ? 'var(--color-pure-white)' : 'var(--color-gray-600)',
})

const inputStyle = { padding: '0.35rem 0.5rem', fontSize: 'var(--text-xs)', border: '1px solid var(--color-gray-300)', borderRadius: '4px' }

function FilterBar({ filters, onChange, products }: { filters: FilterState; onChange: (f: FilterState) => void; products?: any[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
      {/* Preset ranges */}
      {[7, 30, 90].map((d) => (
        <button key={d} style={btnStyle(filters.days === d && !filters.useCustomRange)} onClick={() => onChange({ ...filters, days: d, useCustomRange: false, startDate: '', endDate: '' })}>
          {d}d
        </button>
      ))}

      {/* Custom range toggle */}
      <button style={btnStyle(filters.useCustomRange)} onClick={() => onChange({ ...filters, useCustomRange: !filters.useCustomRange })}>Custom</button>
      {filters.useCustomRange && (
        <>
          <input type="date" value={filters.startDate} onChange={(e) => onChange({ ...filters, startDate: e.target.value })} style={inputStyle} />
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>to</span>
          <input type="date" value={filters.endDate} onChange={(e) => onChange({ ...filters, endDate: e.target.value })} style={inputStyle} />
        </>
      )}

      {/* Aggregation */}
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginLeft: '0.25rem' }}>|</span>
      {(['daily', 'weekly', 'monthly'] as const).map((a) => (
        <button key={a} style={btnStyle(filters.aggregation === a)} onClick={() => onChange({ ...filters, aggregation: a })}>
          {a[0].toUpperCase() + a.slice(1, 3)}
        </button>
      ))}

      {/* Product filter */}
      {products && products.length > 0 && (
        <select
          value={filters.productId}
          onChange={(e) => onChange({ ...filters, productId: e.target.value })}
          style={{ ...inputStyle, minWidth: '140px' }}
        >
          <option value="">All products</option>
          {products.map((p: any) => (
            <option key={p.product_id} value={p.product_id}>{p.title}</option>
          ))}
        </select>
      )}
    </div>
  )
}

// Build query params from filter state
function buildParams(f: FilterState) {
  const params: Record<string, any> = { days: f.days, aggregation: f.aggregation }
  if (f.productId) params.product_id = f.productId
  if (f.useCustomRange && f.startDate) params.start_date = f.startDate
  if (f.useCustomRange && f.endDate) params.end_date = f.endDate
  return params
}

// ─── KPI Card ─────────────────────────────────────────────────────────────

function KpiCard({ label, value, change, prefix, currency }: { label: string; value: number | string; change?: number; prefix?: string; currency?: string }) {
  const changeColor = (change ?? 0) >= 0 ? 'var(--color-lime-green)' : 'var(--color-coral-red)'
  const changeArrow = (change ?? 0) >= 0 ? '+' : ''
  const displayPrefix = currency ? `${currency} ` : (prefix || '')
  return (
    <div className="card" style={{ padding: '1.25rem' }}>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: '0 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
      <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', margin: '0', color: 'var(--color-gray-900)' }}>
        {displayPrefix}{typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {change !== undefined && (
        <p style={{ fontSize: 'var(--text-xs)', color: changeColor, margin: '0.25rem 0 0', fontWeight: 'var(--font-weight-medium)' }}>
          {changeArrow}{change}% vs prior period
        </p>
      )}
    </div>
  )
}

// ─── Funnel Chart ─────────────────────────────────────────────────────────

const FUNNEL_COLORS = ['var(--color-sky-blue)', '#5BA7D9', 'var(--color-sunshine-yellow)', '#F0A030', 'var(--color-lime-green)']

function FunnelChart({ stages }: { stages: { name: string; count: number; dropoff_rate: number | null }[] }) {
  const maxCount = Math.max(...stages.map((s) => s.count), 1)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {stages.map((stage, i) => (
        <div key={stage.name}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-700)' }}>{stage.name}</span>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
              {stage.count.toLocaleString()}
              {stage.dropoff_rate !== null && stage.dropoff_rate > 0 && <span style={{ color: 'var(--color-coral-red)', marginLeft: '0.5rem' }}>-{stage.dropoff_rate}%</span>}
            </span>
          </div>
          <div style={{ height: '28px', background: 'var(--color-gray-100)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${Math.max((stage.count / maxCount) * 100, 8)}%`, height: '100%', background: FUNNEL_COLORS[i] || 'var(--color-sky-blue)', borderRadius: '4px', transition: 'width 0.4s ease' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Overview Section (Growth+) ───────────────────────────────────────────

function OverviewSection({ vendorId, filters }: { vendorId: string; filters: FilterState }) {
  const params = buildParams(filters)
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-overview', vendorId, params],
    queryFn: () => api.analytics.getOverview(vendorId, params),
    staleTime: 60_000, retry: false,
  })

  if (error && (error as any)?.response?.status === 403) return null
  if (isLoading) return <CardSkeleton />

  const overview = data?.data?.data
  if (!overview) return <p style={{ color: 'var(--color-gray-500)' }}>No analytics data yet. Data will appear once your storefront receives traffic.</p>

  const { kpis, time_series, traffic_sources, products } = overview

  // Detect vendor currency from revenue breakdown
  const currencyKeys = Object.keys(kpis.revenue?.currency_breakdown || {})
  const vendorCurrency = currencyKeys.length === 1 ? currencyKeys[0] : ''

  const chartData = (time_series || []).map((d: any) => ({
    period: new Date(d.period).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }),
    bookings: d.bookings || 0, views: d.views || 0, clicks: d.clicks || 0,
  }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
        <KpiCard label="Bookings" value={kpis.bookings?.value || 0} change={kpis.bookings?.change} />
        <KpiCard label="Revenue" value={(kpis.revenue?.value || 0).toFixed(2)} change={kpis.revenue?.change} currency={vendorCurrency || '$'} />
        <KpiCard label="Page Views" value={kpis.page_views?.value || 0} change={kpis.page_views?.change} />
        <KpiCard label="Booking Clicks" value={kpis.booking_clicks?.value || 0} change={kpis.booking_clicks?.change} />
      </div>

      {chartData.length > 0 && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="bookings" stroke="var(--color-sky-blue)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="views" stroke="var(--color-lime-green)" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="clicks" stroke="var(--color-sunshine-yellow)" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {traffic_sources && traffic_sources.length > 0 && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>Traffic Sources</h3>
          <ResponsiveContainer width="100%" height={Math.max(traffic_sources.length * 36, 100)}>
            <BarChart data={traffic_sources} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} allowDecimals={false} />
              <YAxis type="category" dataKey="source" tick={{ fontSize: 12, fill: 'var(--color-gray-700)' }} width={60} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={24}>
                {traffic_sources.map((_: any, i: number) => (
                  <Cell key={i} fill={i === 0 ? 'var(--color-sky-blue)' : 'var(--color-gray-300)'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {products && products.length > 0 && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>Product Breakdown</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)' }}>Product</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)' }}>Views</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)' }}>Bookings</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)' }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p: any) => (
                <tr key={p.product_id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                  <td style={{ padding: '0.5rem 0.75rem', color: 'var(--color-gray-800)' }}>{p.title}</td>
                  <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: 'var(--color-gray-600)' }}>{p.views.toLocaleString()}</td>
                  <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: 'var(--color-gray-600)' }}>{p.bookings.toLocaleString()}</td>
                  <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 'var(--font-weight-medium)' }}>{vendorCurrency || '$'}{p.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Funnel Section (Pro+) ────────────────────────────────────────────────

function FunnelSection({ vendorId, filters }: { vendorId: string; filters: FilterState }) {
  const params = buildParams(filters)
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-funnel', vendorId, params],
    queryFn: () => api.analytics.getFunnel(vendorId, params),
    staleTime: 60_000, retry: false,
  })

  if (error && (error as any)?.response?.status === 403) return null
  if (isLoading) return <CardSkeleton />

  const funnel = data?.data?.data
  if (!funnel) return null

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h3 style={{ fontSize: 'var(--text-base)', margin: 0, fontWeight: 'var(--font-weight-semibold)' }}>Booking Funnel</h3>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
          Overall conversion: {funnel.overall_conversion_rate}%
          {!funnel.stage_4_available && <span style={{ marginLeft: '0.5rem', color: 'var(--color-sunshine-yellow)' }}>(Stage 4 unavailable)</span>}
        </span>
      </div>
      {!funnel.has_enough_data && (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-sunshine-yellow)', marginBottom: '1rem' }}>
          Insufficient data. At least 100 Discovery events needed for accurate funnel analysis.
        </p>
      )}
      <FunnelChart stages={funnel.stages || []} />
      {funnel.highest_dropoff && funnel.highest_dropoff.rate > 0 && (
        <div style={{ marginTop: '1.25rem', padding: '0.75rem 1rem', background: 'rgba(255, 76, 91, 0.06)', border: '1px solid rgba(255, 76, 91, 0.15)', borderRadius: '6px' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)', margin: 0 }}>
            <strong>Biggest opportunity:</strong> {funnel.highest_dropoff.rate}% of users drop off at <strong>{funnel.highest_dropoff.stage}</strong>.
          </p>
        </div>
      )}
    </div>
  )
}

// ─── AI Conversion Section (Pro+) with comparison ─────────────────────────

// ─── Booking Attribution Section (Pro) ────────────────────────────────────

// Display labels for the channels the attribution endpoint returns.
const CHANNEL_LABELS: Record<string, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
  copilot: 'Copilot',
  ai_agent: 'AI Agent',
  google: 'Google',
  bing: 'Bing',
  embed: 'Embedded Widget',
  api: 'API',
  direct: 'Direct',
  other: 'Other',
}

const AI_CHANNELS = ['chatgpt', 'claude', 'gemini', 'perplexity', 'copilot', 'ai_agent']

const channelLabel = (c: string) => CHANNEL_LABELS[c] || c

function AttributionSection({ vendorId, filters }: { vendorId: string; filters: FilterState }) {
  const params = buildParams(filters)
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-attribution', vendorId, params],
    queryFn: () => api.analytics.getAttribution(vendorId, params),
    staleTime: 60_000, retry: false,
  })

  if (error && (error as any)?.response?.status === 403) return null
  if (isLoading) return <CardSkeleton />

  const attribution = data?.data?.data
  if (!attribution) return null

  const channels: any[] = attribution.channels || []
  const totals = attribution.totals || {}

  if (channels.length === 0) {
    return (
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
        No attributed bookings in this period. Source is recorded from the visitor's first page view,
        so bookings made before attribution tracking was enabled are not included.
      </p>
    )
  }

  const chartData = channels.map((c) => ({ ...c, label: channelLabel(c.channel) }))

  // One line per channel over time: pivot [{period, channel, bookings}] into
  // [{period, <channel>: n, ...}] so recharts can render a line per key.
  const seriesRows: any[] = attribution.time_series || []
  const periods = Array.from(new Set(seriesRows.map((r) => r.period))).sort()
  const seriesChannels = Array.from(new Set(seriesRows.map((r) => r.channel)))
  const pivoted = periods.map((period) => {
    const row: Record<string, any> = {
      period: new Date(period).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    }
    seriesChannels.forEach((ch) => {
      row[ch] = seriesRows.find((r) => r.period === period && r.channel === ch)?.bookings || 0
    })
    return row
  })

  const LINE_COLORS = [
    'var(--color-sky-blue)', 'var(--color-lime-green)', 'var(--color-sunshine-yellow)',
    'var(--color-coral-red)', '#5BA7D9', '#9B7EDE',
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', margin: 0 }}>
        Attribution: first-touch, recorded on the booking itself
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
        <KpiCard label="Attributed Bookings" value={totals.bookings || 0} />
        <KpiCard label="AI-Sourced Bookings" value={totals.ai_bookings || 0} />
        <KpiCard label="AI Share" value={`${totals.ai_share || 0}%`} />
        <KpiCard label="Attributed Revenue" value={(totals.revenue || 0).toFixed(2)} prefix="$" />
      </div>

      {/* Channel breakdown */}
      <div className="card" style={{ padding: '1.25rem' }}>
        <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>Bookings by Source</h3>
        <ResponsiveContainer width="100%" height={Math.max(chartData.length * 36, 100)}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} allowDecimals={false} />
            <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: 'var(--color-gray-700)' }} width={110} />
            <Tooltip />
            <Bar dataKey="bookings" radius={[0, 4, 4, 0]} maxBarSize={24}>
              {chartData.map((c, i) => (
                <Cell key={i} fill={AI_CHANNELS.includes(c.channel) ? 'var(--color-sky-blue)' : 'var(--color-gray-300)'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Bookings by source over time */}
      {pivoted.length > 1 && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>Bookings by Source Over Time</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={pivoted}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              {seriesChannels.map((ch, i) => (
                <Line
                  key={ch}
                  type="monotone"
                  dataKey={ch}
                  name={channelLabel(ch)}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Channel table */}
      <div className="card" style={{ padding: '1.25rem' }}>
        <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>Channel Breakdown</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-gray-200)' }}>
              {['Channel', 'Bookings', 'Revenue', 'Share'].map((h, i) => (
                <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '0.5rem', color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {channels.map((c) => (
              <tr key={c.channel} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <td style={{ padding: '0.5rem', color: 'var(--color-gray-900)', fontWeight: AI_CHANNELS.includes(c.channel) ? 'var(--font-weight-semibold)' : 'var(--font-weight-normal)' }}>
                  {channelLabel(c.channel)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{c.bookings.toLocaleString()}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                  {c.currency ? `${c.currency} ` : '$'}{c.revenue.toFixed(2)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--color-gray-500)' }}>{c.share}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AIConversionSection({ vendorId, filters }: { vendorId: string; filters: FilterState }) {
  const params = buildParams(filters)
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-ai-conversion', vendorId, params],
    queryFn: () => api.analytics.getAIConversion(vendorId, params),
    staleTime: 60_000, retry: false,
  })

  if (error && (error as any)?.response?.status === 403) return null
  if (isLoading) return <CardSkeleton />

  const ai = data?.data?.data
  if (!ai) return null

  const comparison = ai.comparison

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {!ai.has_enough_data && (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-sunshine-yellow)' }}>
          Insufficient data. At least 50 AI recommendations needed for accurate conversion metrics.
        </p>
      )}
      {ai.attribution && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>
          Attribution: {ai.attribution.window_days}-day window, {ai.attribution.model.replace('_', '-')}
        </p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
        <KpiCard label="AI Recommendations" value={ai.ai_recommendations || 0} />
        <KpiCard label="AI-to-Click Rate" value={`${ai.ai_to_click_rate || 0}%`} />
        <KpiCard label="AI-to-Booking Rate" value={`${ai.ai_to_booking_rate || 0}%`} />
        <KpiCard label="AI Revenue" value={(ai.ai_revenue || 0).toFixed(2)} prefix="$" />
      </div>

      {/* AI vs Overall Comparison */}
      {comparison && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>AI vs Overall Performance</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--color-gray-500)' }}>Metric</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--color-sky-blue)' }}>AI Channel</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--color-gray-500)' }}>Overall</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <td style={{ padding: '0.5rem 0.75rem' }}>Clicks</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: 'var(--color-sky-blue)', fontWeight: 'var(--font-weight-medium)' }}>{comparison.ai.clicks.toLocaleString()}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{comparison.overall.clicks.toLocaleString()}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <td style={{ padding: '0.5rem 0.75rem' }}>Bookings</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: 'var(--color-sky-blue)', fontWeight: 'var(--font-weight-medium)' }}>{comparison.ai.bookings}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{comparison.overall.bookings}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                <td style={{ padding: '0.5rem 0.75rem' }}>Click-to-Booking Rate</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: 'var(--color-sky-blue)', fontWeight: 'var(--font-weight-medium)' }}>{comparison.ai.click_to_booking_rate}%</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{comparison.overall.click_to_booking_rate}%</td>
              </tr>
              <tr>
                <td style={{ padding: '0.5rem 0.75rem' }}>Revenue</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: 'var(--color-sky-blue)', fontWeight: 'var(--font-weight-medium)' }}>${comparison.ai.revenue.toLocaleString()}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>${comparison.overall.revenue.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Reliability Section (Pro+) with trend + cancellation source ──────────

function ReliabilitySection({ vendorId, filters }: { vendorId: string; filters: FilterState }) {
  const params = buildParams(filters)
  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-reliability', vendorId, params],
    queryFn: () => api.analytics.getReliability(vendorId, params),
    staleTime: 60_000, retry: false,
  })

  if (error && (error as any)?.response?.status === 403) return null
  if (isLoading) return <CardSkeleton />

  const rel = data?.data?.data
  if (!rel) return null

  const alertColors: Record<string, string> = { normal: 'var(--color-lime-green)', warning: 'var(--color-sunshine-yellow)', critical: 'var(--color-coral-red)' }
  const cancellation = rel.cancellation || { rate: rel.cancellation_rate, alert: rel.cancellation_alert, by_source: {} }
  const trend = rel.trend || []

  const trendData = trend.map((t: any) => ({
    week: new Date(t.week).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }),
    cancellation_rate: t.cancellation_rate,
    fulfilment_rate: t.fulfilment_rate,
  }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {!rel.has_enough_data && (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-sunshine-yellow)' }}>Insufficient data. At least 10 bookings needed.</p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '1rem' }}>
        <div className="card" style={{ padding: '1.25rem' }}>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: '0 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cancellation Rate</p>
          <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: alertColors[cancellation.alert] || 'var(--color-gray-900)' }}>{cancellation.rate}%</p>
          {cancellation.by_source && (cancellation.by_source.supplier > 0 || cancellation.by_source.customer > 0) && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: '0.25rem 0 0' }}>
              Supplier: {cancellation.by_source.supplier || 0} | Customer: {cancellation.by_source.customer || 0}
            </p>
          )}
          {cancellation.supplier_rate > 0 && (
            <p style={{ fontSize: 'var(--text-xs)', color: alertColors[cancellation.alert], margin: '0.25rem 0 0' }}>
              Supplier-initiated: {cancellation.supplier_rate}%
            </p>
          )}
        </div>
        <KpiCard label="Fulfilment Rate" value={`${rel.fulfilment_rate}%`} />
        <KpiCard label="Success Rate" value={`${rel.booking_success_rate}%`} />
        <KpiCard label="Response Speed" value={rel.response_speed?.formatted || '--'} />
        <KpiCard label="Total Bookings" value={rel.total_bookings} />
      </div>

      {/* Weekly Trend Chart */}
      {trendData.length > 1 && (
        <div className="card" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem', fontWeight: 'var(--font-weight-medium)' }}>Weekly Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-gray-500)' }} domain={[0, 100]} unit="%" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="fulfilment_rate" name="Fulfilment" stroke="var(--color-lime-green)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cancellation_rate" name="Cancellation" stroke="var(--color-coral-red)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Trust Score */}
      {rel.trust_score !== null && (
        <div className="card" style={{ padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trust Score</p>
            <p style={{ fontSize: 'var(--text-3xl)', fontWeight: 'var(--font-weight-bold)', margin: 0, color: rel.trust_score >= 75 ? 'var(--color-lime-green)' : rel.trust_score >= 50 ? 'var(--color-sunshine-yellow)' : 'var(--color-coral-red)' }}>
              {rel.trust_score}
            </p>
          </div>
          {rel.trust_score_breakdown && (
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              {Object.entries(rel.trust_score_breakdown).map(([key, value]) => (
                <div key={key} style={{ fontSize: 'var(--text-xs)' }}>
                  <span style={{ color: 'var(--color-gray-500)' }}>{key.replace(/_/g, ' ')}: </span>
                  <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>{value as number}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const [filters, setFilters] = useState<FilterState>(defaultFilters)
  const { user, updateSubscriptionTier } = useAuthStore()
  const vendorId = user?.vendorId

  // Sync subscription tier from API so FeatureGate has current data
  const { data: subscriptionStatus } = useQuery({
    queryKey: ['subscription-status', vendorId],
    queryFn: () => api.vendors.getSubscriptionStatus(vendorId!),
    enabled: !!vendorId,
    staleTime: 60_000,
  })

  useEffect(() => {
    const tier = subscriptionStatus?.data?.data?.planTier
    if (tier !== undefined && tier !== user?.subscriptionTier) {
      updateSubscriptionTier(tier)
    }
  }, [subscriptionStatus?.data?.data?.planTier, user?.subscriptionTier, updateSubscriptionTier])

  // Fetch products for the filter dropdown (reuse overview data when available)
  const { data: overviewData } = useQuery({
    queryKey: ['analytics-overview', vendorId, buildParams(filters)],
    queryFn: () => api.analytics.getOverview(vendorId!, buildParams(filters)),
    staleTime: 60_000, retry: false, enabled: !!vendorId,
  })
  const products = overviewData?.data?.data?.products || []

  if (!vendorId) {
    return <p style={{ color: 'var(--color-gray-500)', padding: '2rem' }}>No vendor context found.</p>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)' }}>Analytics</h2>
        <FilterBar filters={filters} onChange={setFilters} products={products} />
      </div>

      <FeatureGate requiredTier={1} title="Basic Analytics Dashboard" description="Upgrade to Growth ($99/mo) to see page views, booking clicks, traffic sources, and booking trends.">
        <OverviewSection vendorId={vendorId} filters={filters} />
      </FeatureGate>

      <FeatureGate requiredTier={2} title="Booking Funnel Insights" description="Upgrade to Pro ($199/mo) to see where potential customers drop off in the booking journey.">
        <FunnelSection vendorId={vendorId} filters={filters} />
      </FeatureGate>

      <FeatureGate requiredTier={2} title="Booking Attribution" description="Upgrade to Pro ($199/mo) to see which channels drive your bookings, including ChatGPT and other AI assistants.">
        <AttributionSection vendorId={vendorId} filters={filters} />
      </FeatureGate>

      <FeatureGate requiredTier={2} title="AI Conversion Metrics" description="Upgrade to Pro ($199/mo) to see how AI recommendations convert into bookings and revenue.">
        <AIConversionSection vendorId={vendorId} filters={filters} />
      </FeatureGate>

      <FeatureGate requiredTier={2} title="Supplier Reliability Metrics" description="Upgrade to Pro ($199/mo) to see cancellation rate, fulfilment rate, and Trust Score breakdown.">
        <ReliabilitySection vendorId={vendorId} filters={filters} />
      </FeatureGate>
    </div>
  )
}
